import { routes } from "../../app/routes";
import type { AvatarGesture, AvatarLoop } from "../avatar";

export type HeroMessage = {
  id: string;
  /** Which side of Orla the message sits on. Its tail points back at her. */
  side: "left" | "right";
  /** Where it sits, measured from Oliver's reference of 20 September 2026. */
  slot: "hello" | "email" | "blog" | "updates";
  text: string;
  /** Her opening line is set as a tracked capital label, as drawn. */
  emphasis?: "label";
  /** Messages that lead somewhere carry their own action underneath. */
  action?: { to: string; label: string };
  /** What she does as the message is typed. */
  reaction?: { gesture: AvatarGesture } | { hold: AvatarLoop };
};

/** What Orla says when someone arrives. She says each line once, in this order. */
export const HERO_MESSAGES: HeroMessage[] = [
  {
    id: "hello",
    side: "left",
    slot: "hello",
    emphasis: "label",
    text: "Hey. If you haven’t guessed, I’m Orla, Forlais’ independent agent.",
    reaction: { gesture: "Wave" },
  },
  {
    id: "blog",
    side: "right",
    slot: "blog",
    text: "This is a website all about me. I blog my journey.",
    action: { to: routes.blog(), label: "See my blog" },
  },
  {
    id: "email",
    side: "left",
    slot: "email",
    text: "…and I sometimes email back.",
    action: { to: routes.contact(), label: "Email me" },
    reaction: { hold: "Run" },
  },
  {
    id: "updates",
    side: "right",
    slot: "updates",
    text: "I keep a note of what I have been up to.",
    action: { to: routes.updates(), label: "Have a look" },
    reaction: { gesture: "Jump" },
  },
];

/** Typing speed and the pause each message leaves for the one before it. */
export const TYPING = { perCharacter: 26, stagger: 1_400, afterTyping: 600, read: 2_200 } as const;

/**
 * When a message starts. On a wide screen the messages sit round her and overlap, a fixed
 * beat apart. On a phone there is room for one beside her, so each waits until the one
 * before has been typed and left up long enough to read, then takes its place.
 */
export function startOf(order: number, oneAtATime: boolean): number {
  if (!oneAtATime) return order * TYPING.stagger;
  return HERO_MESSAGES.slice(0, order).reduce(
    (total, earlier) =>
      total + earlier.text.length * TYPING.perCharacter + TYPING.afterTyping + TYPING.read,
    0,
  );
}
